import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { Button, Card, Field, Input, Select, Textarea, Tag } from "../ui";

const PUBLICOS = [
    { valor: "todos", rotulo: "Todos os usuários" },
    { valor: "proprietarios", rotulo: "Todos os proprietários" },
    { valor: "associados", rotulo: "Todos os associados" },
    { valor: "estagiarios", rotulo: "Todos os estagiários" }
];

/** Aba "E-mails" — enviar em massa, templates e histórico. */
const AdminEmails = ({ avisar }) => {
    const [sub, setSub] = useState("enviar");
    const [templates, setTemplates] = useState([]);
    const [enviados, setEnviados] = useState([]);
    const [erro, setErro] = useState(null);

    // Enviar
    const [envio, setEnvio] = useState({ publico: "todos", assunto: "", corpo_html: "" });
    const [enviando, setEnviando] = useState(false);

    // Template em edição (form)
    const [tpl, setTpl] = useState({ cod_template: null, nome: "", assunto: "", corpo_html: "" });

    const carregarTemplates = () => api.get("/admin/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
    const carregarEnviados = () => api.get("/admin/emails-enviados").then((r) => setEnviados(r.data)).catch(() => {});

    useEffect(() => { carregarTemplates(); carregarEnviados(); }, []);

    const usarTemplate = (cod) => {
        const t = templates.find((x) => String(x.cod_template) === String(cod));
        if (t) setEnvio((e) => ({ ...e, assunto: t.assunto, corpo_html: t.corpo_html }));
    };

    const enviar = async () => {
        if (!envio.assunto.trim() || !envio.corpo_html.trim()) { setErro("Preencha assunto e corpo."); return; }
        if (!window.confirm(`Enviar "${envio.assunto}" para: ${PUBLICOS.find((p) => p.valor === envio.publico)?.rotulo}?`)) return;
        setEnviando(true); setErro(null);
        try {
            const { data } = await api.post("/admin/emails/enviar", envio);
            avisar?.(data.message);
            setEnvio({ publico: "todos", assunto: "", corpo_html: "" });
            carregarEnviados();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível enviar.");
        } finally { setEnviando(false); }
    };

    const salvarTemplate = async () => {
        if (!tpl.nome.trim() || !tpl.assunto.trim() || !tpl.corpo_html.trim()) { setErro("Preencha nome, assunto e corpo do template."); return; }
        setErro(null);
        try {
            if (tpl.cod_template) await api.put(`/admin/email-templates/${tpl.cod_template}`, tpl);
            else await api.post("/admin/email-templates", tpl);
            avisar?.("Template salvo.");
            setTpl({ cod_template: null, nome: "", assunto: "", corpo_html: "" });
            carregarTemplates();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível salvar o template.");
        }
    };

    const excluirTemplate = async (cod) => {
        if (!window.confirm("Excluir este template?")) return;
        try { await api.delete(`/admin/email-templates/${cod}`); carregarTemplates(); } catch { /* ignore */ }
    };

    return (
        <div>
            <div className="nav-pilula" style={{ marginBottom: 12 }}>
                {[["enviar", "Enviar e-mail"], ["templates", "Templates"], ["enviados", "Enviados"]].map(([k, r]) => (
                    <button key={k} type="button" className={sub === k ? "ativo" : ""} onClick={() => setSub(k)}>{r}</button>
                ))}
            </div>

            {erro && <p className="erro">{erro}</p>}

            {sub === "enviar" && (
                <Card>
                    <h3 className="card-title">Enviar e-mail em massa</h3>
                    <div className="grid-form">
                        <Field label="Público">
                            <Select value={envio.publico} onChange={(e) => setEnvio((v) => ({ ...v, publico: e.target.value }))}>
                                {PUBLICOS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
                            </Select>
                        </Field>
                        <Field label="Usar template">
                            <Select defaultValue="" onChange={(e) => usarTemplate(e.target.value)}>
                                <option value="">— nenhum —</option>
                                {templates.map((t) => <option key={t.cod_template} value={t.cod_template}>{t.nome}</option>)}
                            </Select>
                        </Field>
                        <Field label="Assunto" className="span-2">
                            <Input value={envio.assunto} onChange={(e) => setEnvio((v) => ({ ...v, assunto: e.target.value }))} />
                        </Field>
                        <Field label="Corpo (HTML)" className="span-2">
                            <Textarea style={{ minHeight: 160 }} value={envio.corpo_html} onChange={(e) => setEnvio((v) => ({ ...v, corpo_html: e.target.value }))} />
                        </Field>
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <Button onClick={enviar} disabled={enviando}>{enviando ? "Enviando..." : "Enviar agora"}</Button>
                    </div>
                    <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                        Sem SMTP configurado no servidor, os envios são simulados (registrados) — em produção defina o SMTP.
                    </p>
                </Card>
            )}

            {sub === "templates" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Card>
                        <h3 className="card-title">{tpl.cod_template ? "Editar template" : "Novo template"}</h3>
                        <div className="grid-form">
                            <Field label="Nome" className="span-2"><Input value={tpl.nome} onChange={(e) => setTpl((t) => ({ ...t, nome: e.target.value }))} /></Field>
                            <Field label="Assunto" className="span-2"><Input value={tpl.assunto} onChange={(e) => setTpl((t) => ({ ...t, assunto: e.target.value }))} /></Field>
                            <Field label="Corpo (HTML)" className="span-2"><Textarea style={{ minHeight: 140 }} value={tpl.corpo_html} onChange={(e) => setTpl((t) => ({ ...t, corpo_html: e.target.value }))} /></Field>
                        </div>
                        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <Button onClick={salvarTemplate}>{tpl.cod_template ? "Salvar" : "Criar"}</Button>
                            {tpl.cod_template && <Button variante="secondary" onClick={() => setTpl({ cod_template: null, nome: "", assunto: "", corpo_html: "" })}>Cancelar</Button>}
                        </div>
                    </Card>
                    <div className="scroll-x">
                        <table className="table">
                            <thead><tr><th>Nome</th><th>Assunto</th><th>Atualizado</th><th>Ações</th></tr></thead>
                            <tbody>
                                {templates.map((t) => (
                                    <tr key={t.cod_template}>
                                        <td>{t.nome}</td><td>{t.assunto}</td><td className="num">{t.atualizado_em}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <Button variante="secondary" onClick={() => setTpl(t)}>Editar</Button>
                                                <Button variante="danger" onClick={() => excluirTemplate(t.cod_template)}>Excluir</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {templates.length === 0 && <tr><td colSpan="4" className="text-muted">Nenhum template.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {sub === "enviados" && (
                <div className="scroll-x">
                    <table className="table">
                        <thead><tr><th>Quando</th><th>Assunto</th><th>Público</th><th className="num">Total</th><th className="num">Enviados</th><th>Falhas</th></tr></thead>
                        <tbody>
                            {enviados.map((e) => (
                                <tr key={e.cod_envio}>
                                    <td className="num" style={{ whiteSpace: "nowrap" }}>{e.criado_em}</td>
                                    <td>{e.assunto}</td>
                                    <td>{e.publico}</td>
                                    <td className="num">{e.total}</td>
                                    <td className="num">{e.enviados}</td>
                                    <td>{e.falhas > 0 ? <Tag tom="danger">{e.falhas}</Tag> : "0"}</td>
                                </tr>
                            ))}
                            {enviados.length === 0 && <tr><td colSpan="6" className="text-muted">Nenhum envio ainda.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminEmails;
